import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const OVERLAY_SHADOW = 'shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />

      <div
        className={`relative bg-white rounded-xl border border-slate-200 ${OVERLAY_SHADOW} w-full ${SIZE[size]} max-h-[90vh] flex flex-col`}
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100">
          <h2 className="text-[15px] font-semibold text-slate-900 tracking-tight pr-2 truncate">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-md p-1 flex-shrink-0"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="px-4 py-4 overflow-y-auto flex-1 text-[13px] text-slate-700">{children}</div>

        {footer && (
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/60 rounded-b-xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

interface ModalFooterProps {
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'danger';
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  onCancel,
  onConfirm,
  confirmText = 'Speichern',
  cancelText = 'Abbrechen',
  confirmDisabled = false,
  loading = false,
  variant = 'primary',
}) => {
  return (
    <>
      <Button variant="secondary" onClick={onCancel} disabled={loading}>
        {cancelText}
      </Button>
      <Button variant={variant} onClick={onConfirm} disabled={confirmDisabled || loading}>
        {loading ? 'Wird gespeichert…' : confirmText}
      </Button>
    </>
  );
};

export default Modal;
